import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FaDownload, FaPaperclip } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import api from "../../api/apiClient";
import nodeService from "../../services/nodeService";
import { toast } from "react-toastify";
import {
  fetchFilteredGraphData,
  removeGraphNode,
} from "../../redux/slices/graphSlice";
import DeleteConfirmationModal from "./DeleteConfirmationPopup";
import OptimizedQuillEditor from "./OptimizedQuillEditor";
import { useUserId } from "../../Utils/helpers";
import { baseURL } from "../../env/env";
import Select from "react-select";
import { processLink } from "../../Utils/LinkUtils";
import graphService from "../../services/graphService";
import { MdDelete } from "react-icons/md";

const textAreaFields = [
  "Action",
  "Question",
  "Description",
  "Content",
  "Meaning",
  "Office Address",
];

const excludedKeys = ["_oid", "_global", "_id", "instance_of"];

const InfoTab = ({
  data,
  isEditing,
  handleDeleteRelation,
  handlePreview,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  editorData,
  cyschema,
  nodeProps,
  handleInputChange,
  handleFileChange,
  handleUpload,
  cyRef,
  setNewEdge,
  setNewNode,
  fetchTopicNodesCount,
  extractFilename,
  setNodeProps,
  selectedSearchId,
  filePath,
  setFilepath,
  isFileLoading,
  fileExists,
  setFileExists,
  checkIfFileExists,
}) => {
  const currentRole = useSelector((state) => state.auth.currentRole);
  const [fromRelation, setFromRelation] = useState("");
  const [toRelation, setToRelation] = useState("");
  const [fromInstance, setFromInstance] = useState("");
  const [toInstance, setToInstance] = useState("");
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [fromRelationValue, setFromRelationValue] = useState("");
  const [toRelationValue, setToRelationValue] = useState("");
  const [fromInstances, setFromInstances] = useState([]);
  const [toInstances, setToInstances] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingFromInstances, setIsLoadingFromInstances] = useState(false);
  const [isLoadingToInstances, setIsLoadingToInstances] = useState(false);
  const [selectedFromOption, setSelectedFromOption] = useState(
    fromOptions.find((option) => option.value === fromRelation) || null
  );

  const fromInstanceOptions = fromInstances?.map((instance) => ({
    value: instance.value,
    label: instance.text,
  }));

  const [selectedFromInstance, setSelectedFromInstance] = useState(
    fromInstanceOptions.find((option) => option.value === fromInstance) || null
  );
  const [selectedToOption, setSelectedToOption] = useState(
    toOptions.find((option) => option.value === toRelation) || null
  );
  const [selectedIoInstance, setSelectedToInstance] = useState(null);
  const [isFromLoading, setIsFromLoading] = useState(false);
  const [isToLoading, setIsToLoading] = useState(false);
  const [isDeleteAttachment, setIsDeleteAttachment] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [fieldLabelMaps, setFieldLabelMaps] = useState({});
  const dispatch = useDispatch();
  const userId = useUserId();
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const defaultLayout = {
    randomize: false,
    name: "cola",
    animate: true,
    refresh: 2,
    maxSimulationTime: 100,
    fit: true,
    handleDisconnected: true,
    infinite: false,
    ungrabifyWhileSimulating: false,
    nodeDimensionsIncludeLabels: true,
    convergenceThreshold: 0.1,
    nodeSpacing: function (node) {
      return 30;
    },
    ready: () => {},
    stop: () => {},
    unconstrIter: 10,
    userConstIter: 10,
    allConstIter: 0,
    gridifyIter: 5,
    edgeLength: (edge) => {
      const nodes = edge.connectedNodes();
      const degs = nodes.minDegree();

      // Base length based on node degree
      let baseLength = 100 + 65 * Math.sqrt(degs - 1);

      // Get the label and measure its length
      const edgeData = edge.data();
      const label = edgeData.label || "";
      const labelLength = label.length;

      // Calculate edge length dynamically based on label length
      const lengthAdjustment = labelLength * 5; // Adjust multiplier as needed for spacing
      return baseLength + lengthAdjustment;
    },
  };

  const getNodeForRangeOrDomain = useCallback(async (direction, className) => {
    try {
      const response = await api.post("/instances/search", {
        direction: direction,
        rangeOrDomainClassesNames: className,
      });
      return response.data.nodes;
    } catch (error) {
      console.error("Error fetching nodes:", error);
      return [];
    }
  }, []);

  const getRelationOptions = useCallback(
    (relClassNode, direction, selfNodeId) => {
      const rangeClass = relClassNode
        .connectedEdges(
          direction === "from" ? "[label='RANGE']" : "[label='DOMAIN']"
        )
        .connectedNodes("[class_kind='node']");
      const rangeSubClasses = rangeClass.predecessors("[class_kind='node']");
      const rangeClassesNames = rangeSubClasses
        .map((n) => n.data()?.props.name)
        .concat([rangeClass.data()?.props.name]);

      return { rangeClassesNames };
    },
    []
  );

  // Create an object with keys and their labels
  const createFieldLabelObject = (fields, className) => {
    const fieldLabelObject = {};

    if (className === "PlaybookRule") {
      const orderedKeys = ["actiontext", "questiontext", "name", "desc"];
      orderedKeys.forEach((key) => {
        if (fields[key]) {
          fieldLabelObject[key] = fields[key].label;
        }
      });

      // Include any remaining fields that weren't explicitly ordered
      Object.keys(fields).forEach((key) => {
        if (!fieldLabelObject[key]) {
          fieldLabelObject[key] = fields[key].label;
        }
      });
    } else {
      Object.keys(fields).forEach((key) => {
        fieldLabelObject[key] = fields[key].label;
      });
    }

    setFieldLabelMaps(fieldLabelObject);
  };

  const getLabelText = (originalLabel) => {
    switch (originalLabel) {
      case "Literal Text":
        return "Content";
      case "Condition":
        return "Meaning";
      default:
        return originalLabel;
    }
  };

  useEffect(() => {
    const calculateAvailableRelations = () => {
      const className = data.class;
      const classNode = cyschema?.$(
        `node[class_kind='node'][name='${className}']`
      );
      createFieldLabelObject(classNode.data().props.fields, className);
      const cls_isas = classNode.successors("edge.ISA").connectedNodes();

      const newAvailRels = {
        from: cls_isas
          .connectedEdges('edge[label="DOMAIN"]')
          .sources()
          .filter((n) => getRelationClassScopes(n).has("admin")),
        to: cls_isas
          .connectedEdges('edge[label="RANGE"]')
          .sources()
          .filter((n) => getRelationClassScopes(n).has("admin")),
      };

      const cmpNodesByName = (a, b) => {
        const A = (
          a.data().display ||
          a.data().props.name ||
          a.data().cls ||
          ""
        ).toUpperCase();
        const B = (
          b.data().display ||
          b.data().props.name ||
          b.data().cls ||
          ""
        ).toUpperCase();
        return A.localeCompare(B);
      };

      const createOptionsFromNodes = (nodes) => {
        return nodes.sort(cmpNodesByName).map((node) => ({
          value: node.id(),
          text: node.data().name,
        }));
      };

      setFromOptions(createOptionsFromNodes(newAvailRels.from));
      setToOptions(createOptionsFromNodes(newAvailRels.to));
      setSelectedFromOption(null);
      setSelectedFromInstance(null);
      setSelectedToOption(null);
      setSelectedToInstance(null);
      setFromRelationValue("");
      setToRelationValue("");
    };

    if (cyschema) {
      setFileExists(null);
      calculateAvailableRelations();
      checkIfFileExists(data?.props?.attachment);
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  }, [data, cyschema, containerRef]);

  const getRelationClassImmediateParents = (_relClassNode) => {
    const relClass = cyschema.$id(_relClassNode.id());
    const parentClasses = cyschema
      .collection([relClass])
      .outgoers("edge[label='REL_ISA']")
      .targets();
    return parentClasses;
  };

  const getRelationClassScopes = (_relClassNode) => {
    const relClass = cyschema.$id(_relClassNode.id());
    const relScopes = new Set(relClass.data().props.scope);
    const parentClasses = getRelationClassImmediateParents(relClass);

    if (parentClasses.length > 1) {
      let parentScopes = parentClasses.reduce(
        (scopes, n) => new Set([...scopes, ...getRelationClassScopes(n)]),
        new Set()
      );
      return new Set([...relScopes, ...parentScopes]);
    } else {
      return relScopes;
    }
  };

  const handleRelationChange = async (direction, relationId, e) => {
    if (direction === "from") {
      setFromRelation(relationId);
      setFromInstance("");
      setIsLoadingFromInstances(true); // Start loading for "from" instances
      setIsLoadingToInstances(false);
    } else {
      setToRelation(relationId);
      setToInstance("");
      setIsLoadingToInstances(true);
    }

    const relClassNode = cyschema.$id(relationId);
    const { rangeClassesNames } = getRelationOptions(
      relClassNode,
      direction,
      data.id
    );

    const nodes = await getNodeForRangeOrDomain(direction, rangeClassesNames);

    const instances = nodes.map((node) => ({
      value: node.id,
      text: `${node.props.name || ""} :${node.class} (${node.id})`,
    }));

    if (direction === "from") {
      setFromInstances(instances);
      setIsLoadingFromInstances(false); // End loading after data is set
      setIsLoadingToInstances(false);
    } else {
      setToInstances(instances);
      setIsLoadingFromInstances(false); // End loading after data is set
      setIsLoadingToInstances(false);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const deleteAttachmentFunction = async (filepath) => {
    // Perform the DELETE request to delete the attachment from the backend
    const response = await api.delete(`/document/${filepath}/`);

    if (response.status !== 200) {
      toast.error("Failed to delete attachment.");
      return false;
    }
    setFilepath(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the file input field
    }
    toast.success("Attachment deleted successfully.");
    return true;
  };

  const deleteAttachment = async () => {
    const nodeId = data.id;

    try {
      // Sanitize and encode the file name
      const sanitizedFileName = attachmentToDelete;
      // .replace(/\s+/g, "_")
      // .replace(/[()]/g, "");
      const encodedFileName = encodeURIComponent(sanitizedFileName);

      let updatedNodeProps = { ...nodeProps, attachment: "" }; // Initialize updatedNodeProps

      if (fileExists) {
        const isDeleteSuccess = deleteAttachmentFunction(encodedFileName);
        if (isDeleteSuccess) {
          // Update the nodeProps on the server
          const updateResponse = await api.post(
            `/instances/${nodeId}/`,
            updatedNodeProps,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const updatedData = updateResponse.data.new_props;

          // Fetch the topic nodes count and update the node props
          await fetchTopicNodesCount(nodeId);
          setNodeProps(updatedData);

          // Dispatch to fetch filtered graph data if necessary
          if (!selectedSearchId) {
            dispatch(fetchFilteredGraphData({ userId }));
          }
        }
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      toast.error("Failed to delete attachment.");
    }
  };

  const handleDeleteAttachment = (filename) => {
    setIsDeleteAttachment(true);
    toggleDeleteModal();
    setAttachmentToDelete(filename);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await nodeService.deleteNodeInstance(data.id);
      setIsDeleteModalOpen(false);
      // dispatch(fetchFilteredGraphData({ userId: userId }));
      dispatch(removeGraphNode(data.id));
      toast.success("Node deleted successfully");
    } catch (error) {
      console.error("Failed to delete node:", error);
      toast.error("Failed to delete node");
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const onCreateRelation = async (
    { direction, relationName, srcId, instanceId, value },
    setLoading // Pass the loading state setter as a parameter
  ) => {
    try {
      setLoading(true);

      const rel_attrs = {};
      if (value) {
        rel_attrs.value = value;
      }

      const requestPayload = {
        relation_name: relationName,
        rel_attrs,
      };

      if (direction === "from") {
        requestPayload.src_id = data.id.toString();
        requestPayload.dst_id = instanceId;
      } else if (direction === "to") {
        requestPayload.src_id = instanceId;
        requestPayload.dst_id = data.id.toString();
      }

      const response = await api.post("/relations/", requestPayload);

      if (response.status !== 200) {
        throw new Error("Failed to create relation");
      }

      const newNodeResponse = await graphService.graphSearch(userId, {
        includeClasses: [],
        includeTopics: [],
        includeKeywords: [],
        includeManualAndExpand: [],
        includeIds: [instanceId],
      });

      const { nodes, edges } = newNodeResponse;
      const newNode = nodes?.find((node) => node.id === instanceId);
      const newEdge = edges?.find(
        (edge) =>
          (edge.source === data.id && edge.target === instanceId) ||
          (edge.source === instanceId && edge.target === data.id)
      );

      setNewEdge((prev) => [...(prev ?? []), newEdge]);
      setNewNode((prev) => [...(prev ?? []), newNode]);

      const cyNode = {
        data: {
          id: newNode.id,
          label: newNode.display,
          color: newNode.colour || "#666",
        },
      };

      const cyEdge = {
        data: {
          id: newEdge.id,
          source: newEdge.source,
          target: newEdge.target,
          label:
            newEdge.class === "IsAnswerFor" && newEdge.props.value
              ? `${newEdge.display} = ${newEdge.props.value}`
              : newEdge.display || "",
        },
      };
      cyRef.add([cyNode, cyEdge]);
      cyRef.layout(defaultLayout).run();

      setSelectedFromOption(null);
      setSelectedFromInstance(null);
      setSelectedToOption(null);
      setSelectedToInstance(null);
      setFromRelationValue("");
      setToRelationValue("");
      toast.success("Relation created successfully!");
    } catch (error) {
      console.error("Error creating relation:", error);
      toast.error("Error creating relation!");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFrom = () => {
    const relationName = fromOptions.find(
      (option) => option.value === fromRelation
    )?.text;

    const params = {
      direction: "from",
      relationName,
      srcId: fromRelation,
      instanceId: fromInstance,
    };

    if (fromRelationValue) {
      params.value = fromRelationValue;
    }

    onCreateRelation(params, setIsFromLoading); // Use setIsFromLoading
  };

  const handleCreateTo = () => {
    const relationName = toOptions.find(
      (option) => option.value === toRelation
    )?.text;

    const params = {
      direction: "to",
      relationName,
      srcId: toRelation,
      instanceId: toInstance,
    };

    if (toRelationValue) {
      params.value = toRelationValue;
    }

    onCreateRelation(params, setIsToLoading); // Use setIsToLoading
  };

  const handleDownload = async (value) => {
    const response = await fetch(
      `${baseURL}/uploads/${encodeURIComponent(extractFilename(value))}`
    );

    if (!response.ok) {
      toast.error("Failed to download file");
      return;
    }

    const blob = await response.blob(); // Convert response to Blob
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob); // Create a Blob URL
    link.download = extractFilename(value); // Specify the filename
    link.click();
    URL.revokeObjectURL(link.href); // Cleanup the Blob URL
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "4px 8px", // Reduce padding to make it smaller
      fontSize: "0.875rem", // Reduce font size
      height: "30px", // Adjust height for a smaller input box
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0", // Remove extra padding inside the container
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px", // Reduce padding around the dropdown indicator (arrow)
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "4px", // Reduce padding around the clear indicator (X button)
    }),
    menu: (base) => ({
      ...base,
      fontSize: "0.875rem", // Reduce font size of dropdown items
    }),
    option: (base) => ({
      ...base,
      padding: "6px 8px", // Reduce option padding
    }),
  };

  const renderFields = (
    nodeProps,
    isEditing,
    currentRole,
    handleInputChange,
    handleFileChange,
    handleUpload,
    handlePreview,
    handleDownload,
    handleDeleteAttachment,
    processLink,
    isFileLoading,
    fileExists,
    textAreaFields
  ) => {
    return Object.keys(fieldLabelMaps).length > 0
      ? Object.keys(fieldLabelMaps).map((fieldKey) => {
          const value = nodeProps[fieldKey];
          const displayKey =
            getLabelText(fieldLabelMaps[fieldKey]) ||
            fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1);

          return (
            <li key={fieldKey} className="prop-field">
              <strong>{displayKey}:</strong>
              <div>
                <Form.Group
                  controlId={`form-${fieldKey}`}
                  className="text-wrap"
                >
                  {isEditing ? (
                    textAreaFields.includes(displayKey) ? (
                      // If field is a textarea, render textarea
                      <Form.Control
                        as="textarea"
                        value={value || ""}
                        rows={4}
                        onChange={(e) =>
                          handleInputChange(fieldKey, e.target.value)
                        }
                      />
                    ) : fieldKey === "attachment" ? (
                      // If field is attachment, render file input and upload button
                      <Form.Group controlId="form-attachment" className="mt-3">
                        <Form.Control
                          type="file"
                          onChange={handleFileChange}
                          ref={fileInputRef}
                        />
                        <div className="d-flex justify-content-end">
                          {filePath ? (
                            <Button
                              variant="danger"
                              onClick={() => deleteAttachmentFunction(filePath)}
                              className="del-rem mt-2"
                            >
                              Delete
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={handleUpload}
                              className="create-button mt-2"
                            >
                              Upload
                            </Button>
                          )}
                        </div>
                      </Form.Group>
                    ) : (
                      // Default to text input
                      <Form.Control
                        type="text"
                        value={value || ""}
                        onChange={(e) =>
                          handleInputChange(fieldKey, e.target.value)
                        }
                      />
                    )
                  ) : // If not editing
                  fieldKey === "attachment" ? (
                    isFileLoading ? (
                      // Show spinner if file is loading
                      <div style={{ marginLeft: "auto" }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : fileExists && value ? (
                      // If file exists, render file link, download, and delete options
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid lightblue",
                          padding: "10px",
                          borderRadius: "4px",
                          gap: "7px",
                        }}
                      >
                        <span
                          style={{
                            color: "#007bff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            display: "flex",
                            alignItems: "center",
                            overflowWrap: "anywhere",
                            gap: "5px",
                          }}
                          onClick={() => handlePreview(extractFilename(value))}
                        >
                          <FaPaperclip style={{ marginRight: "5px" }} />
                          {extractFilename(value)}
                        </span>
                        <Button
                          variant="light"
                          className="d-flex align-items-center"
                          onClick={() => handleDownload(value)}
                          style={{ marginLeft: "auto" }}
                          title="Download Document"
                        >
                          <FaDownload
                            style={{ cursor: "pointer", color: "#191e3c" }}
                          />
                        </Button>
                        {currentRole !== "viewer" && (
                          <Button
                            variant="light"
                            className="d-flex align-items-center"
                            size="sm"
                            onClick={() => handleDeleteAttachment(value)}
                            style={{ marginLeft: "10px" }}
                            title="Delete Document"
                          >
                            <MdDelete className="text-danger" size={20} />
                          </Button>
                        )}
                      </div>
                    ) : (
                      // If no file attached
                      "No file attached"
                    )
                  ) : // If not an attachment field, render the value or 'Not available'
                  value ? (
                    <span
                      className="text-wrap"
                      dangerouslySetInnerHTML={{
                        __html: Array.isArray(value)
                          ? value.length > 0
                            ? processLink(value.join(", "))
                            : "Not available"
                          : processLink(value || "Not available"),
                      }}
                    />
                  ) : (
                    "Not available"
                  )}
                </Form.Group>
              </div>
            </li>
          );
        })
      : Object.entries(nodeProps)
          .filter(([key]) => !excludedKeys.includes(key))
          .map(([key, value]) => {
            const displayKey =
              key === "desc"
                ? "Description"
                : key === "text"
                ? "Content"
                : key === "Literal Text"
                ? "Content"
                : key === "Condition"
                ? "Meaning"
                : key.charAt(0).toUpperCase() + key.slice(1);

            return (
              <li key={key} className="prop-field">
                <strong>{displayKey}:</strong>
                <div>
                  {key === "attachment" ? (
                    isEditing ? (
                      <Form.Group controlId={`form-${key}`} className="mt-3">
                        <Form.Control type="file" onChange={handleFileChange} />
                        <div className="d-flex justify-content-end">
                          {filePath ? (
                            <Button
                              variant="danger"
                              onClick={() => deleteAttachmentFunction(filePath)}
                              className="del-rem mt-2"
                            >
                              Delete
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={handleUpload}
                              className="create-button mt-2"
                            >
                              Upload
                            </Button>
                          )}
                        </div>
                      </Form.Group>
                    ) : isFileLoading ? (
                      <div style={{ marginLeft: "auto" }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : fileExists && value ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid lightblue",
                          padding: "10px",
                          borderRadius: "4px",
                          gap: "7px",
                        }}
                      >
                        <span
                          style={{
                            color: "#007bff",
                            cursor: "pointer",
                            textDecoration: "underline",
                            display: "flex",
                            alignItems: "center",
                            overflowWrap: "anywhere",
                            gap: "5px",
                          }}
                          onClick={() => handlePreview(extractFilename(value))}
                        >
                          <FaPaperclip style={{ marginRight: "5px" }} />
                          {extractFilename(value)}
                        </span>
                        <Button
                          variant="light"
                          className="d-flex align-items-center"
                          onClick={() => handleDownload(value)}
                          style={{ marginLeft: "auto" }}
                          title="Download Document"
                        >
                          <FaDownload
                            style={{
                              cursor: "pointer",
                              color: "#191e3c",
                            }}
                          />
                        </Button>
                        {currentRole !== "viewer" && (
                          <Button
                            variant="light"
                            className="d-flex align-items-center"
                            size="sm"
                            onClick={() => handleDeleteAttachment(value)}
                            style={{ marginLeft: "10px" }}
                            title="Delete Document"
                          >
                            <MdDelete className="text-danger" size={20} />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span>No file attached</span>
                    )
                  ) : (
                    <Form.Group controlId={`form-${key}`} className="text-wrap">
                      {isEditing ? (
                        textAreaFields.includes(displayKey) ? (
                          // Render a textarea for specific fields
                          <Form.Control
                            as="textarea"
                            value={value || ""}
                            rows={4}
                            onChange={(e) =>
                              handleInputChange(key, e.target.value)
                            }
                          />
                        ) : (
                          // Render a regular text input for other fields
                          <Form.Control
                            type="text"
                            value={value || ""}
                            onChange={(e) =>
                              handleInputChange(key, e.target.value)
                            }
                          />
                        )
                      ) : (
                        <div>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: Array.isArray(value)
                                ? value.length > 0
                                  ? processLink(value.join(", "))
                                  : "Not available"
                                : processLink(value || "Not available"),
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  )}
                </div>
              </li>
            );
          });
  };

  return (
    <>
      <div className="side-panel-header">
        <h5>
          <strong style={{ color: "black" }}>
            {data.class}
            <span> --&gt;</span>
          </strong>{" "}
          <span style={{ color: "#696969" }}>
            {data.props?.name || "Playbook Demo"}
          </span>
        </h5>
      </div>
      <div ref={containerRef} className="side-panel-body overflowy-auto">
        <p>
          {data?.relations?.length > 0 &&
          data.relations.some(
            (relation) => relation.relatedNodeName !== "Unknown"
          )
            ? "This instance has relations:"
            : "This instance has no relations."}
        </p>
        <ul>
          {data?.relations?.map((relation, index) => {
            if (relation.relatedNodeName === "Unknown") {
              return null;
            }
            return (
              <li key={index} className="lighter-bullet">
                {relation.className} {relation.relationType}{" "}
                {relation.relatedNodeName}
                {currentRole !== "viewer" && (
                  <span
                    className="delete-link"
                    onClick={() => handleDeleteRelation(relation.relationId)}
                  >
                    {" "}
                    Delete
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <ul>
          {renderFields(
            nodeProps,
            isEditing,
            currentRole,
            handleInputChange,
            handleFileChange,
            handleUpload,
            handlePreview,
            handleDownload,
            handleDeleteAttachment,
            processLink,
            isFileLoading,
            fileExists,
            textAreaFields
          )}
        </ul>

        {currentRole !== "viewer" && (
          <div className="second-section">
            <div className="d-flex justify-content-end">
              <Button
                variant="danger"
                className="mt-3 del-rem"
                onClick={() => handleDeleteClick()}
              >
                Delete this {data.class}
              </Button>
              {!isEditing ? (
                <Button
                  variant="primary"
                  className="mr-2 mt-3 create-button"
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="success"
                    className="mr-2 mt-3 create-button"
                    onClick={handleSaveClick}
                  >
                    Save
                  </Button>
                  <Button
                    variant="secondary"
                    className="mt-3 cance-button"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
            <Form.Group controlId="formDropdown1" className="mt-3">
              <Form.Label>New relation from this instance</Form.Label>
              <Select
                menuPlacement="auto"
                options={fromOptions.map((option) => ({
                  value: option.value,
                  label: option.text,
                }))}
                value={selectedFromOption}
                onChange={(selectedOption) => {
                  setSelectedFromOption(selectedOption);
                  handleRelationChange("from", selectedOption?.value);
                }}
                placeholder="Select relation"
                isDisabled={!fromOptions || fromOptions.length === 0}
                styles={customStyles}
                isClearable
              />
              <div className="mt-3 px-1">to instance</div>
              <Select
                menuPlacement="auto"
                options={fromInstanceOptions}
                value={selectedFromInstance}
                onChange={(selectedOption) => {
                  setSelectedFromInstance(selectedOption);
                  setFromInstance(selectedOption?.value);
                }}
                placeholder={
                  isLoadingFromInstances ? "Loading..." : "Select instance"
                }
                isDisabled={
                  !fromInstanceOptions || fromInstanceOptions.length === 0
                }
                styles={customStyles}
                isClearable
                isLoading={isLoadingFromInstances}
              />
              {selectedFromOption?.label === "IsAnswerFor" && (
                <Form.Group controlId="formValueInput" className="mt-2">
                  <Form.Label>Value</Form.Label>
                  <Form.Control
                    type="text"
                    value={fromRelationValue}
                    onChange={(e) => setFromRelationValue(e.target.value)}
                  />
                </Form.Group>
              )}

              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  className="mt-2 create-button"
                  onClick={handleCreateFrom}
                  disabled={!fromRelation || !fromInstance || isFromLoading}
                >
                  {isFromLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </Form.Group>
            <Form.Group controlId="formDropdown2" className="mt-3">
              <Form.Label>New relation to this instance</Form.Label>
              <Select
                menuPlacement="auto"
                options={toOptions.map((option) => ({
                  value: option.value,
                  label: option.text,
                }))}
                value={selectedToOption}
                onChange={(selectedOption) => {
                  setSelectedToOption(selectedOption);
                  handleRelationChange("to", selectedOption?.value);
                }}
                placeholder="Select relation"
                isDisabled={!toOptions || toOptions.length === 0}
                styles={customStyles}
                isClearable
              />
              <div className="mt-3 px-1">from instance</div>
              <Select
                menuPlacement="auto"
                options={toInstances.map((instance) => ({
                  value: instance.value,
                  label: instance.text,
                }))}
                value={selectedIoInstance}
                onChange={(selectedOption) => {
                  setSelectedToInstance(selectedOption);
                  setToInstance(selectedOption?.value);
                }}
                placeholder={
                  isLoadingToInstances ? "Loading..." : "Select instance"
                }
                isDisabled={!toInstances || toInstances.length === 0}
                styles={customStyles}
                isClearable
                isLoading={isLoadingToInstances}
              />
              {selectedToOption?.label === "IsAnswerFor" ? (
                <Form.Group controlId="formValueInputTo" className="mt-2">
                  <Form.Label>Value</Form.Label>
                  <Form.Control
                    type="text"
                    value={toRelationValue}
                    onChange={(e) => setToRelationValue(e.target.value)}
                  />
                </Form.Group>
              ) : null}
              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  className="mt-2 create-button"
                  onClick={handleCreateTo}
                  disabled={!toRelation || !toInstance || isToLoading}
                >
                  {isToLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </Form.Group>
          </div>
        )}
        {fileExists && data.props?.attachment && (
          <div className="mt-4 h-500">
            <OptimizedQuillEditor initialContent={editorData} />
          </div>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        toggle={toggleDeleteModal}
        onConfirm={isDeleteAttachment ? deleteAttachment : handleDeleteConfirm}
        name={
          isDeleteAttachment
            ? extractFilename(data.props?.attachment)
            : data.props?.name || data.class
        }
        isLoading={isDeleting}
        deletionType={isDeleteAttachment ? "attachment" : "node"}
      />
    </>
  );
};

export default InfoTab;
